import { useTranslation } from 'react-i18next';
import { CartProductType } from '@steelbuy/data-access';
import {
    formatCurrency,
    CartEntryModel,
    AnyMaterialModel,
    Product,
    formatNumber,
    formatNumberForDimensions,
    getCurrencyFromRegion,
    getDimensionUnit,
    getRegion,
} from '@steelbuy/ts-shared';
import { shouldShowTolerance, getQualityDisplayValue } from '@steelbuy/util';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';

import { CartSummaryHeader } from '../cart-summary/cart-summary-header/CartSummaryHeader';
import { useMaterialProperties } from '../material/Material';

import './CheckoutCartSummary.scss';

export type CheckoutCartSummaryProps = {
    cartData: CartEntryModel;
};

export const CheckoutCartSummary = ({ cartData }: CheckoutCartSummaryProps) => {
    const { t } = useTranslation(['domainModel', 'uiDomain']);

    const isPackage = cartData.productType === CartProductType.PACKAGE;
    const material = cartData.product.material as AnyMaterialModel;
    const materialTypeProperty = isPackage ? cartData.product.material : cartData.product.material.materialType;
    const isDynamicDeliveryPriceEnabled = useIsVendorFeatureSupported('DYNAMIC_DELIVERY_PRICE');

    const materialProperties = useMaterialProperties(isPackage ? cartData.product : material, ['materialType']);

    const materialDimensions = isPackage
        ? { thickness: undefined, width: undefined, thicknessType: undefined }
        : cartData.product.materialDimensions;

    const { thickness, width, thicknessType } = materialDimensions;

    const widthText = width
        ? t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
              value: formatNumberForDimensions(width),
          })
        : '';
    const lengthText =
        'length' in materialDimensions
            ? t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                  value: formatNumberForDimensions(materialDimensions.length),
              })
            : undefined;

    const thicknessText = thickness
        ? [
              t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                  value: formatNumberForDimensions(thickness),
              }),
          ]
        : [];
    if (thicknessType) {
        thicknessText.push(t(`domainModel:material.thicknessType.value.${thicknessType}`));
    }
    const dimensionsText = [
        thicknessText.length ? thicknessText.join(' ') : '',
        widthText ? `x ${widthText}` : '',
        lengthText ? `x ${lengthText}` : '',
    ]
        .filter(Boolean)
        .join(' ');

    const materialType = t(`material.materialType.value.${materialTypeProperty}`);

    const tolerance =
        !isPackage && 'tolerance' in material && shouldShowTolerance(material.tolerance)
            ? t(`material.tolerance.value.${material.tolerance}`)
            : '';

    const productType = isPackage
        ? t(`domainModel:material.product.value.${Product.PACKAGE}`)
        : t(`domainModel:material.product.value.${cartData.product.material.product}`);

    const weight =
        cartData.product.weight && cartData.product.tradeUnit
            ? t(`material.tradeUnit.value.${cartData.product.tradeUnit}.quantity`, {
                  count: cartData.product.weight,
                  formatted: formatNumber(cartData.product.weight),
              })
            : '';

    const totalPricingField = isDynamicDeliveryPriceEnabled ? 'totalIncludingVat' : 'totalExcludingVat';
    const hasOfferPrice = !isPackage && cartData.product.offerPricing;

    const offerPricingTotal =
        hasOfferPrice &&
        formatCurrency(
            cartData.product.offerPricing?.[totalPricingField].value ?? 0,
            cartData.product.offerPricing?.currencyCode ?? getCurrencyFromRegion(getRegion())
        );

    const oldPricingTotal =
        cartData.product.oldPricing &&
        formatCurrency(
            cartData.product.oldPricing?.[totalPricingField]?.value ?? 0,
            cartData.product.oldPricing?.currencyCode ?? getCurrencyFromRegion(getRegion())
        );

    const applicableTotalPriceLabel = cartData.product.pricing?.priceIncludingEstimatedDelivery?.value
        ? t('uiDomain:listingBuyerDetailsPrice.totalInclDelivery')
        : t('uiDomain:listingBuyerDetailsPrice.totalExclDelivery');

    const totalPriceLabel = isDynamicDeliveryPriceEnabled
        ? applicableTotalPriceLabel
        : t('uiDomain:listingBuyerDetailsPrice.totalExclVat');

    const totalPrice = cartData.product.pricing[totalPricingField].value;

    const totalApplicablePrice = formatCurrency(totalPrice, cartData.product.pricing.currencyCode);

    return (
        <div className="checkout-cart-summary">
            <div className="checkout-cart-summary__header-wraper">
                <CartSummaryHeader
                    symbol={isPackage ? cartData.productType.toLowerCase() : material.product.toLowerCase()}
                    firstLine={materialProperties.map((property) => property.label).join(', ')}
                    secondLine={`${productType}, ${materialType}, ${getQualityDisplayValue(
                        cartData.product.quality,
                        cartData.product.prime,
                        t
                    )}, ${weight} `}
                    thirdLine={[dimensionsText, tolerance].filter(Boolean).join(', ')}
                />
                <div className="checkout-cart-summary__header-wraper__pricing">
                    <span className="checkout-cart-summary__header-wraper__total-excluding-pricing__label">
                        {totalPriceLabel}:
                    </span>
                    {hasOfferPrice || cartData.product.oldPricing ? (
                        <span className="checkout-cart-summary__header-wraper__total-excluding-pricing__value">
                            <span className="checkout-cart-summary__header-wraper__total-excluding-pricing__value--old">
                                {oldPricingTotal ?? totalApplicablePrice}
                            </span>
                            <span className="checkout-cart-summary__header-wraper__total-excluding-pricing__value--new">
                                {offerPricingTotal || totalApplicablePrice}
                            </span>
                        </span>
                    ) : (
                        <span className="checkout-cart-summary__header-wraper__total-excluding-pricing__value">
                            {totalApplicablePrice}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};
